import { useQuery } from '@tanstack/react-query'
import { ClipboardList, FileText, MonitorPlay } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { FormField } from '@/components/forms/FormField'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { classesQuery } from '@/features/academic/api'
import { useAuth } from '@/features/auth/useAuth'
import { coursesQuery } from '@/features/lms/api'
import { lmsBase } from '@/features/lms/status'
import type { CourseCard } from '@/features/lms/types'
import { subjectDotStyle } from '@/features/timetable/week'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

/** Online learning: the courses a teacher teaches, or every course for a manager (by class). */
export function LmsCoursesPage() {
  const { t } = useTranslation('lms')
  useDocumentTitle('Online learning')
  const { can, portal } = useAuth()
  const manager = can('lms.manage') && portal === 'admin'
  const [params, setParams] = useSearchParams()
  const classId = Number(params.get('class')) || undefined
  const classes = useQuery({ ...classesQuery(), enabled: manager })
  const courses = useQuery(coursesQuery(manager ? classId : undefined))
  const base = lmsBase(portal)

  return (
    <>
      <PageHeader
        title={t('Online learning')}
        description={
          manager
            ? t('Every course of the school: lessons, resources and homework published online.')
            : t('Your courses: publish lessons and resources, set homework and grade it.')
        }
        breadcrumbs={[{ label: t('Online learning') }]}
        homeHref={portal === 'staff' ? '/staff' : '/admin'}
      />
      {manager ? (
        <Card className="mb-6 p-4">
          <FormField label={t('Class')} className="sm:max-w-xs">
            <Select
              placeholder={t('All classes')}
              value={classId ?? ''}
              onChange={(e) => setParams(e.target.value ? { class: e.target.value } : {}, { replace: true })}
              options={(classes.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
            />
          </FormField>
        </Card>
      ) : null}
      {courses.isPending ? (
        <LoadingState />
      ) : courses.error ? (
        <ErrorState onRetry={() => void courses.refetch()} />
      ) : courses.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<MonitorPlay />}
            title={t('No courses yet')}
            description={
              manager
                ? t('Courses come from each class’s subjects. Add subjects to the classes first.')
                : t('Your courses appear once you teach a subject in the timetable or are class teacher.')
            }
          />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {courses.data.map((c) => (
            <li key={`${c.section.id}-${c.subject.id}`}>
              <CourseTile course={c} href={`${base}/courses/${c.section.id}/${c.subject.id}`} />
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function CourseTile({ course, href }: { course: CourseCard; href: string }) {
  const { t } = useTranslation('lms')
  return (
    <Link
      to={href}
      className="group block h-full rounded-card border border-line bg-surface p-4 shadow-card transition hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-raised"
    >
      <div className="flex items-start gap-3">
        <span
          className="mt-1 size-3 shrink-0 rounded-full bg-brand-500"
          style={subjectDotStyle(course.subject.color)}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-ink group-hover:text-brand-700">{course.subject.name}</p>
          <p className="text-xs text-muted">{course.section.name}</p>
        </div>
        {course.toGrade > 0 ? (
          <Badge tone="warning" size="sm">
            {t('{{count}} to grade', { count: course.toGrade })}
          </Badge>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <FileText className="size-3.5 text-muted" aria-hidden="true" />
          {t('{{count}} contents', { count: course.materials.published })}
          {course.materials.drafts ? (
            <span className="text-muted">· {t('{{count}} drafts', { count: course.materials.drafts })}</span>
          ) : null}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ClipboardList className="size-3.5 text-muted" aria-hidden="true" />
          {t('{{count}} open assignments', { count: course.assignments.open })}
        </span>
      </div>
    </Link>
  )
}
